import { FC, useCallback } from 'react'
import { Button, Icon, Notification } from '@mondra/ui-components'
import Collapsible from 'react-collapsible'
import InputItem from '../../../../components/InputItem'
import { IComponentMaterialRow, IPackagingProcessingActivityOption } from '../types'
import {
  EI_NAME_MAX_LENGTH,
  EXACT_MATCH_OPTIONS,
  formatProcessingActivityLabel,
  fromExactMatchValue,
  toExactMatchValue,
  toNumberOrNull,
} from '../utils'

interface MaterialRowProps {
  formik: any
  includesProcessingImpacts: boolean
  index: number
  onDelete: (index: number) => void
  processingActivities: IPackagingProcessingActivityOption[]
  row: IComponentMaterialRow
}

/**
 * One Made of Materials row: its two read-only taxonomy references, then the four editable
 * combination fields. A component rather than inline JSX so every handler can be memoised and
 * closed over its own row index.
 */
const MaterialRow: FC<MaterialRowProps> = ({
  formik,
  includesProcessingImpacts,
  index,
  onDelete,
  processingActivities,
  row,
}) => {
  const handleDelete = useCallback(() => {
    onDelete(index)
  }, [onDelete, index])

  // Selects are coerced on the way in so the stored value stays a number or null and never the
  // -1 placeholder or a raw string, which would also make the dirty check fire spuriously.
  const handleProcessingActivityChange = useCallback(
    e => {
      formik.setFieldValue(
        `madeOfMaterials[${index}].defaultProcessingActivityId`,
        toNumberOrNull(e.target.value)
      )
    },
    [formik, index]
  )

  const handleExactMatchChange = useCallback(
    e => {
      formik.setFieldValue(
        `madeOfMaterials[${index}].isExactMatch`,
        toExactMatchValue(e.target.value)
      )
    },
    [formik, index]
  )

  const triggerLabel = row.packagingMaterialFunctionalUnitName
    ? row.packagingMaterialCategoryName
      ? `${row.packagingMaterialCategoryName} - ${row.packagingMaterialFunctionalUnitName}`
      : row.packagingMaterialFunctionalUnitName
    : `${row.packagingMaterialCategoryName} *`

  return (
    <Collapsible
      trigger={
        <div className="collapsible_trigger">
          <span>{triggerLabel}</span>
          <Icon className="collapsible_icon" type="chevronDown" />
        </div>
      }
    >
      {includesProcessingImpacts && (
        <div className="px-4 pb-2">
          <Notification
            description="This material's emission factor already includes packaging production impacts (e.g. forming, manufacturing). A default processing activity may double-count them."
            isToast={false}
            label="Note:"
            type="warning"
          />
        </div>
      )}
      <div className="component-taxonomy-row-inputs px-4 pb-2">
        <InputItem
          defaultValue={row.packagingMaterialCategoryName}
          description="Material taxonomy category"
          disabled
          formik={formik}
          formikName={`madeOfMaterials[${index}].packagingMaterialCategoryName`}
          name="Category"
          onChange={formik.handleChange}
          type="input"
        />
        <InputItem
          defaultValue={
            row.packagingMaterialFunctionalUnitName || `${row.packagingMaterialCategoryName} *`
          }
          description="Specific material or all materials in the category"
          disabled
          formik={formik}
          formikName={`madeOfMaterials[${index}].packagingMaterialFunctionalUnitName`}
          name="Material"
          onChange={formik.handleChange}
          type="input"
        />
        <InputItem
          defaultValue={row.defaultProcessingActivityId ?? -1}
          description="The published packaging processing activity this material defaults to."
          formik={formik}
          formikName={`madeOfMaterials[${index}].defaultProcessingActivityId`}
          name="Processing Name"
          onChange={handleProcessingActivityChange}
          options={processingActivities.map(pa => ({
            label: formatProcessingActivityLabel(pa),
            value: pa.id,
          }))}
          type="select"
        />
        <InputItem
          addonTitle="%"
          defaultValue={row.defaultRecycledContent ?? ''}
          description="Default recycled content as a percentage, 0 to 100. Leave empty if not stated."
          formik={formik}
          formikName={`madeOfMaterials[${index}].defaultRecycledContent`}
          name="Default Recycled Content"
          onChange={formik.handleChange}
          type="input"
        />
        <InputItem
          defaultValue={row.eiName ?? ''}
          description={`The Ecoinvent activity name for this material. Up to ${EI_NAME_MAX_LENGTH} characters.`}
          formik={formik}
          formikName={`madeOfMaterials[${index}].eiName`}
          name="EI Name"
          onChange={formik.handleChange}
          type="input"
        />
        <InputItem
          defaultValue={fromExactMatchValue(row.isExactMatch)}
          description="Whether the Ecoinvent activity is an exact match for this material or a proxy."
          formik={formik}
          formikName={`madeOfMaterials[${index}].isExactMatch`}
          name="Exact Match or Proxy"
          onChange={handleExactMatchChange}
          options={EXACT_MATCH_OPTIONS}
          type="select"
        />
      </div>
      <div className="mt-2 flex justify-end px-4 pb-4">
        <Button iconType="close" onClick={handleDelete} variant="danger">
          Delete
        </Button>
      </div>
    </Collapsible>
  )
}

export default MaterialRow
